import React from 'react';
import SEO from '../../components/SEO';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Shield, 
  Building2,
  ArrowLeft,
  Clock,
  Calendar,
  CheckCircle,
  AlertTriangle,
  FileText,
  Users,
  Euro,
  Send,
  Sparkles
} from 'lucide-react';

export default function RepondreAppelOffreArticle() {
  return (
    <>
      <SEO 
        title="Comment Répondre à un Appel d'Offres | Guide Étape par Étape"
        description="Guide complet pour répondre efficacement à un appel d'offres public : préparation, constitution du dossier, soumission."
        path="/blog/repondre-appel-offre"
      />
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <span className="font-bold text-xl">Mon DCE Express</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b">
        <div className="container mx-auto px-4 py-3">
          <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700">
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>
        </div>
      </div>

      {/* Article */}
      <article>
        <header className="bg-gradient-to-b from-amber-50 to-white py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                Guide
              </span>
              <span className="flex items-center gap-1 text-slate-500 text-sm">
                <Calendar className="w-4 h-4" />
                15 janvier 2026
              </span>
              <span className="flex items-center gap-1 text-slate-500 text-sm">
                <Clock className="w-4 h-4" />
                15 min de lecture
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Comment Répondre à un Appel d'Offres : Guide Étape par Étape
            </h1>
            
            <p className="text-xl text-slate-600">
              De l'analyse du DCE à la soumission de l'offre : toutes les étapes pour 
              maximiser vos chances de remporter un marché public.
            </p>
          </div>
        </header>

        {/* Content */}
        <div className="container mx-auto px-4 max-w-4xl py-12">
          
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-lg text-slate-700 mb-6">
              <strong>Répondre à un appel d'offres public</strong> peut sembler complexe, mais avec 
              une méthodologie rigoureuse, vous maximisez vos chances de succès. Ce guide vous accompagne 
              à chaque étape, de la découverte du marché jusqu'au dépôt de votre offre.
            </p>
          </section>

          {/* Timeline */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-8">Les 8 étapes pour répondre à un appel d'offres</h2>

            <div className="space-y-8">
              {/* Étape 1 */}
              <div className="relative pl-8 border-l-4 border-violet-200">
                <div className="absolute -left-4 w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Télécharger et lire le DCE</h3>
                <p className="text-slate-600 mb-4">
                  Commencez par télécharger l'intégralité du <strong>Dossier de Consultation des Entreprises</strong>. 
                  Lisez d'abord le Règlement de Consultation (RC) qui explique les règles du jeu.
                </p>
                <Card className="bg-violet-50 border-violet-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-violet-800">
                      <strong>Documents clés à lire en priorité :</strong> RC (règles), CCTP (cahier des charges), 
                      CCAP (conditions contractuelles)
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Étape 2 */}
              <div className="relative pl-8 border-l-4 border-violet-200">
                <div className="absolute -left-4 w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Évaluer votre éligibilité</h3>
                <p className="text-slate-600 mb-4">
                  Avant d'investir du temps, vérifiez que vous remplissez toutes les conditions :
                </p>
                <ul className="space-y-2 mb-4">
                  {[
                    'Certifications requises (ISO, Qualibat, RGE...)',
                    'Références similaires demandées',
                    'Capacité financière minimum',
                    'Zone géographique d\'intervention',
                    'Assurances professionnelles'
                  ].map((item, i) => (
                    <li key={`item-${i}`} className="flex items-center gap-2 text-slate-600">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Étape 3 */}
              <div className="relative pl-8 border-l-4 border-violet-200">
                <div className="absolute -left-4 w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Analyser les critères de notation</h3>
                <p className="text-slate-600 mb-4">
                  Le RC indique les <strong>critères de sélection</strong> et leur pondération. Typiquement :
                </p>
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Euro className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <p className="font-bold">Prix</p>
                      <p className="text-2xl font-bold text-green-600">40-60%</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                      <p className="font-bold">Valeur technique</p>
                      <p className="text-2xl font-bold text-blue-600">30-50%</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Clock className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                      <p className="font-bold">Délais</p>
                      <p className="text-2xl font-bold text-amber-600">5-15%</p>
                    </CardContent>
                  </Card>
                </div>
                <p className="text-sm text-slate-500">
                  Adaptez votre stratégie selon cette pondération : si le technique compte 50%, 
                  investissez dans un mémoire détaillé.
                </p>
              </div>

              {/* Étape 4 */}
              <div className="relative pl-8 border-l-4 border-violet-200">
                <div className="absolute -left-4 w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Préparer le dossier administratif</h3>
                <p className="text-slate-600 mb-4">
                  Rassemblez tous les documents obligatoires. Une pièce manquante = offre rejetée !
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { doc: 'DC1', desc: 'Lettre de candidature' },
                    { doc: 'DC2', desc: 'Déclaration du candidat' },
                    { doc: 'Kbis', desc: 'Extrait de moins de 3 mois' },
                    { doc: 'Attestations', desc: 'URSSAF, fiscales' },
                    { doc: 'Assurance RC', desc: 'Attestation en cours' },
                    { doc: 'Références', desc: 'Marchés similaires réalisés' },
                  ].map((item) => (
                    <Card key={item.doc} className="border-slate-200">
                      <CardContent className="p-3 flex items-center gap-3">
                        <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-sm font-bold">
                          {item.doc}
                        </span>
                        <span className="text-sm text-slate-600">{item.desc}</span>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Étape 5 */}
              <div className="relative pl-8 border-l-4 border-violet-200">
                <div className="absolute -left-4 w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold">5</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Rédiger le mémoire technique</h3>
                <p className="text-slate-600 mb-4">
                  C'est souvent le document <strong>le plus décisif</strong>. Structurez-le selon les 
                  sous-critères du RC et soyez concret :
                </p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Méthodologie</strong> : Comment allez-vous réaliser la prestation ?</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Moyens humains</strong> : Qui intervient ? Quelles compétences ?</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Moyens matériels</strong> : Quels équipements utilisez-vous ?</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Planning</strong> : Détaillez les phases et jalons</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Valeur ajoutée</strong> : Qu'apportez-vous de plus que les concurrents ?</span>
                  </li>
                </ul>
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-amber-800">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      <strong>Conseil :</strong> Évitez le copier-coller ! Personnalisez chaque réponse 
                      en reprenant les termes du CCTP. Les évaluateurs le remarquent immédiatement.
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Étape 6 */}
              <div className="relative pl-8 border-l-4 border-violet-200">
                <div className="absolute -left-4 w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold">6</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Établir l'offre de prix</h3>
                <p className="text-slate-600 mb-4">
                  Complétez le <strong>BPU</strong> (Bordereau des Prix Unitaires) et/ou la <strong>DPGF</strong> 
                  (Décomposition du Prix Global et Forfaitaire) fournis dans le DCE.
                </p>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="p-4">
                      <h4 className="font-bold text-green-900 mb-2">✓ À faire</h4>
                      <ul className="text-sm text-green-800 space-y-1">
                        <li>• Vérifier tous les prix unitaires</li>
                        <li>• Inclure tous les coûts (déplacements, etc.)</li>
                        <li>• Garder une marge raisonnable</li>
                      </ul>
                    </CardContent>
                  </Card>
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="p-4">
                      <h4 className="font-bold text-red-900 mb-2">✗ À éviter</h4>
                      <ul className="text-sm text-red-800 space-y-1">
                        <li>• Prix anormalement bas (rejet)</li>
                        <li>• Oublier des postes</li>
                        <li>• Erreurs de calcul</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Étape 7 */}
              <div className="relative pl-8 border-l-4 border-violet-200">
                <div className="absolute -left-4 w-8 h-8 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold">7</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Vérifier et signer les documents</h3>
                <p className="text-slate-600 mb-4">
                  Avant de soumettre, faites une <strong>checklist complète</strong> :
                </p>
                <ul className="space-y-2 mb-4">
                  {[
                    'Tous les documents demandés sont présents',
                    'L\'Acte d\'Engagement est signé',
                    'Les prix sont cohérents et vérifiés',
                    'Le mémoire répond à tous les critères',
                    'Les formats de fichiers sont respectés (PDF, etc.)',
                    'La signature électronique est valide (si requise)'
                  ].map((item, i) => (
                    <li key={`item-${i}`} className="flex items-center gap-2 text-slate-600">
                      <div className="w-5 h-5 border-2 border-slate-300 rounded flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Étape 8 */}
              <div className="relative pl-8 border-l-4 border-green-400">
                <div className="absolute -left-4 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">8</div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Déposer l'offre</h3>
                <p className="text-slate-600 mb-4">
                  Soumettez votre offre sur la <strong>plateforme de dématérialisation</strong> indiquée 
                  dans le RC. Attention aux délais !
                </p>
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4">
                    <p className="text-red-800 font-medium">
                      <AlertTriangle className="w-5 h-5 inline mr-2" />
                      <strong>IMPORTANT :</strong> Déposez votre offre au moins <strong>24 heures avant</strong> la 
                      date limite. Les problèmes techniques de dernière minute sont fréquents !
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* After submission */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Après le dépôt</h2>
            
            <div className="space-y-4">
              <Card>
                <CardContent className="p-4 flex items-start gap-4">
                  <Clock className="w-6 h-6 text-violet-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-slate-900">Délai d'analyse</h4>
                    <p className="text-slate-600">L'acheteur a généralement 1 à 3 mois pour analyser les offres.</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-start gap-4">
                  <Users className="w-6 h-6 text-violet-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-slate-900">Négociation possible</h4>
                    <p className="text-slate-600">Certaines procédures prévoient une phase de négociation. Préparez vos arguments.</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-start gap-4">
                  <Send className="w-6 h-6 text-violet-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-slate-900">Notification</h4>
                    <p className="text-slate-600">Vous recevrez une notification d'attribution ou de rejet. En cas de rejet, demandez le rapport d'analyse pour progresser.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Conclusion</h2>
            <p className="text-slate-600 mb-4">
              Répondre à un appel d'offres demande de la rigueur et de l'organisation. En suivant 
              cette méthodologie et en préparant des offres de qualité, vous augmentez significativement 
              vos chances de succès.
            </p>
            <p className="text-slate-600">
              N'oubliez pas : la première étape est de <strong>trouver les bons marchés</strong>. 
              Avec Mon DCE Express, notre IA vous recommande les appels d'offres les plus pertinents 
              pour votre entreprise, vous permettant de concentrer vos efforts sur les vraies opportunités.
            </p>
          </section>

        </div>

        {/* CTA */}
        <section className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Trouvez vos prochains marchés
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              Notre IA analyse 6+ sources et vous recommande les appels d'offres adaptés à votre profil.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100">
                <Sparkles className="w-5 h-5 mr-2" />
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </section>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="container mx-auto px-4 text-center text-sm">
          <p>© 2026 Mon DCE Express - Tous droits réservés</p>
        </div>
      </footer>
    </div>
    </>
  );
}
